import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { formatDateTime } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Tooltip } from "@impero-com/ui";
import { cx } from "class-variance-authority";
import { Control, useWatch } from "react-hook-form";

import { TestingActivityForm, SAMPLES_KEY } from "../../schema";

import * as theme from "./ViewedIndicator.css";

interface ViewedIndicatorProps {
    control: Control<TestingActivityForm>;
    sampleFieldIndex: number;
    disabled: boolean;
}

export function ViewedIndicator({
    control,
    sampleFieldIndex,
    disabled,
}: ViewedIndicatorProps) {
    const status = useWatch({
        control,
        name: `${SAMPLES_KEY}.${sampleFieldIndex}.viewed`,
        disabled,
    });

    switch (status.type) {
        case "Viewed":
            return <ViewedIcon date={status.value.at} />;
        case "NotViewed":
            return <NotViewedIcon />;
        default:
            assertNever(status);
    }
}

interface ViewedIconProps {
    date: Date;
}

function ViewedIcon({ date }: ViewedIconProps) {
    const { t } = useTranslation();
    return (
        <div className={theme.viewedIconContainer}>
            <Tooltip
                title={t("ControlTestingActivityView_ViewedTooltip", {
                    date: formatDateTime(date),
                })}
            >
                <EyeOutlined className={cx(theme.icon, theme.viewedIcon)} />
            </Tooltip>
        </div>
    );
}

function NotViewedIcon() {
    const { t } = useTranslation();
    return (
        <div className={theme.viewedIconContainer}>
            <Tooltip title={t("ControlTestingActivityView_NotViewedTooltip")}>
                <EyeInvisibleOutlined
                    className={cx(theme.icon, theme.notViewedIcon)}
                />
            </Tooltip>
        </div>
    );
}
